/**
 * Core-100 R2 — CORE-Q GAP-002/003/004: the person sees who can see their Passport, on what basis and
 * for how long — including relationships that ENDED, stated as such — and who recently looked.
 * Server-computed (`my_passport_audience`, `passport_view_events`); this only renders it.
 */
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';

interface AudienceRow { basis: string; shop_name: string | null; who: string; lifetime: string; ended_at: string | null }
interface ViewRow { id: string; viewer_name_snapshot: string | null; basis: string; shop_name_snapshot: string | null; viewed_at: string }

export function describeAudience(r: AudienceRow): string {
  if (r.basis === 'current_shop') return `${r.shop_name ?? 'Your shop'}: ${r.who} — ${r.lifetime}`;
  // Oct 20 addendum P2: organisation administrators, named per organisation (shop_name carries the organisation name).
  if (r.basis === 'organization') return `${r.shop_name ?? 'Your organization'}: ${r.who} — ${r.lifetime}`;
  if (r.basis === 'connect_discoverable') return `Connect: ${r.who} — ${r.lifetime}`;
  if (r.basis === 'ended') return `${r.shop_name ?? 'A former shop'}: access ended${r.ended_at ? ` on ${new Date(r.ended_at).toLocaleDateString()}` : ''}`;
  // Core-100 R4 W2 (CORE-Q): the applicant basis is stated with its lifetime and, once closed, as an ended fact; a
  // suspended membership is stated as paused, not as ended.
  if (r.basis === 'applicant') return `${r.shop_name ?? 'A shop you applied to'} (application): ${r.who} — ${r.lifetime}`;
  if (r.basis === 'applicant_ended') return `${r.shop_name ?? 'A shop you applied to'} (application): access ${r.lifetime}${r.ended_at ? ` on ${new Date(r.ended_at).toLocaleDateString()}` : ''}`;
  if (r.basis === 'suspended') return `${r.shop_name ?? 'Your shop'}: ${r.who} — ${r.lifetime}`;
  return r.basis;
}

/** How a recorded view's basis reads to the person (server-recorded by record_passport_view). */
export function viewBasisLabel(basis: string): string {
  if (basis === 'connect_discoverable') return 'via Connect';
  if (basis === 'applicant') return 'as a shop you applied to';
  if (basis === 'organization_admin') return "as your organization's administrator";
  return 'as your shop';
}

export function PassportAudienceCard() {
  const { data } = useQuery({
    queryKey: ['passport-audience'],
    queryFn: async () => {
      const [{ data: audience }, { data: views }] = await Promise.all([
        supabase.rpc('my_passport_audience' as never),
        supabase.from('passport_view_events' as never)
          .select('id, viewer_name_snapshot, basis, shop_name_snapshot, viewed_at')
          .order('viewed_at', { ascending: false }).limit(20),
      ]);
      return {
        audience: ((audience ?? []) as unknown) as AudienceRow[],
        views: ((views ?? []) as unknown) as ViewRow[],
      };
    },
  });
  return (
    <Card data-testid="passport-audience">
      <CardHeader>
        <CardTitle className="text-base">Who can see your Passport</CardTitle>
        <CardDescription>Decided by OptiBay's access rules; listed here so you can see them.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <ul className="space-y-1">
          {(data?.audience ?? []).map((r, i) => <li key={i}>{describeAudience(r)}</li>)}
          {data && data.audience.length === 0 && <li className="text-muted-foreground">Only you.</li>}
        </ul>
        {(data?.views.length ?? 0) > 0 && (
          <div>
            <p className="text-xs font-medium text-muted-foreground">Recent views</p>
            <ul className="space-y-1" data-testid="passport-views">
              {data!.views.map((v) => (
                <li key={v.id} className="text-xs">
                  {v.viewer_name_snapshot ?? 'Someone'}{v.shop_name_snapshot ? ` (${v.shop_name_snapshot})` : ''}
                  {' · '}{viewBasisLabel(v.basis)}{' · '}{new Date(v.viewed_at).toLocaleDateString()}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
